import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Edit2, Trash2, Upload, Save, X, Shirt, Image as ImageIcon,
  Package, GripVertical, ClipboardList, AlertTriangle, BarChart3, Mail, RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: '',
  description: '',
  category: 'bahiana',
  base_price: 0,
  images: [],
  thumbnail: '',
  sizes_available: [],
  colors_available: [],
  is_custom_order: false,
  estimated_delivery_days: 14,
  low_stock_threshold: 3,
  featured: false,
  tags: [],
  variants: [],
};

const EMPTY_VARIANT = { size: '', color: '', price_adjustment: 0, stock: 0, sku: '' };

const CATEGORY_OPTIONS = [
  { value: 'bahiana', label: 'Bahiana' },
  { value: 'passista', label: 'Passista' },
  { value: 'bateria', label: 'Bateria' },
  { value: 'orixas', label: 'Orixás' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'custom', label: 'Custom' },
];

const FULFILLMENT_STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];

const PAYMENT_BADGE = {
  pending: 'bg-yellow-500/20 text-yellow-300',
  paid: 'bg-emerald-500/20 text-emerald-300',
  refunded: 'bg-stone-500/20 text-stone-300',
};

export default function AdminCostumes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/admin/dashboard';

  const [tab, setTab] = useState('products'); // 'products' | 'orders'
  const [costumes, setCostumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [sizesInput, setSizesInput] = useState('');
  const [colorsInput, setColorsInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  useEffect(() => {
    if (user && !['super_admin', 'admin'].includes(user.role)) {
      toast.error('Access Denied: Admin only');
      navigate('/admin/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadCostumes();
  }, []);

  useEffect(() => {
    if (tab === 'orders' && orders.length === 0 && !loadingOrders) {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const authHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadCostumes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/costumes`, { params: { limit: 200 } });
      setCostumes(res.data.costumes || []);
    } catch (e) {
      toast.error('Failed to load costumes');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await axios.get(`${API}/costumes/admin/orders`, {
        headers: authHeaders(),
        params: { limit: 100 },
      });
      setOrders(res.data.orders || []);
    } catch (e) {
      toast.error('Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/costumes/admin/orders/${orderId}/status`,
        null,
        { headers: authHeaders(), params: { fulfillment_status: newStatus } }
      );
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, fulfillment_status: newStatus } : o)));
      toast.success('Order status updated');
    } catch (e) {
      toast.error('Failed to update order');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSizesInput('');
    setColorsInput('');
    setTagsInput('');
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || '',
      description: c.description || '',
      category: c.category || 'bahiana',
      base_price: c.base_price ?? 0,
      images: c.images || [],
      thumbnail: c.thumbnail || '',
      sizes_available: c.sizes_available || [],
      colors_available: c.colors_available || [],
      is_custom_order: !!c.is_custom_order,
      estimated_delivery_days: c.estimated_delivery_days ?? 14,
      low_stock_threshold: c.low_stock_threshold ?? 3,
      featured: !!c.featured,
      tags: c.tags || [],
      variants: (c.variants || []).map((v) => ({ ...EMPTY_VARIANT, ...v })),
    });
    setSizesInput((c.sizes_available || []).join(', '));
    setColorsInput((c.colors_available || []).join(', '));
    setTagsInput((c.tags || []).join(', '));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const splitCSV = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/costumes/admin/upload-image`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      return res.data.url;
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = [];
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadImage(f);
        urls.push(url);
      }
      setForm((prev) => ({
        ...prev,
        images: [...prev.images, ...urls],
        thumbnail: prev.thumbnail || urls[0],
      }));
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const removeImage = (url) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((u) => u !== url),
      thumbnail: prev.thumbnail === url ? (prev.images.find((u) => u !== url) || '') : prev.thumbnail,
    }));
  };

  const setThumbnail = (url) => {
    setForm((prev) => ({ ...prev, thumbnail: url }));
  };

  // dnd-kit sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setForm((prev) => {
      const oldIndex = prev.images.indexOf(active.id);
      const newIndex = prev.images.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return { ...prev, images: arrayMove(prev.images, oldIndex, newIndex) };
    });
  }, []);

  // Variants
  const addVariant = () => {
    setForm((prev) => ({ ...prev, variants: [...prev.variants, { ...EMPTY_VARIANT }] }));
  };
  const updateVariant = (idx, field, value) => {
    setForm((prev) => {
      const next = [...prev.variants];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, variants: next };
    });
  };
  const removeVariant = (idx) => {
    setForm((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (form.base_price < 0) return toast.error('Price must be ≥ 0');

    const payload = {
      ...form,
      base_price: Number(form.base_price) || 0,
      estimated_delivery_days: Number(form.estimated_delivery_days) || 14,
      low_stock_threshold: Number(form.low_stock_threshold) || 0,
      sizes_available: splitCSV(sizesInput),
      colors_available: splitCSV(colorsInput),
      tags: splitCSV(tagsInput),
      variants: form.variants
        .filter((v) => (v.size || '').trim() || (v.color || '').trim())
        .map((v) => ({
          size: (v.size || '').trim(),
          color: (v.color || '').trim() || null,
          price_adjustment: Number(v.price_adjustment) || 0,
          stock: Number(v.stock) || 0,
          sku: (v.sku || '').trim() || null,
        })),
    };

    try {
      if (editingId) {
        await axios.put(`${API}/costumes/admin/${editingId}`, payload, { headers: authHeaders() });
        toast.success('Costume updated');
      } else {
        await axios.post(`${API}/costumes/admin/create`, payload, { headers: authHeaders() });
        toast.success('Costume created');
      }
      closeForm();
      loadCostumes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/costumes/admin/${id}`, { headers: authHeaders() });
      toast.success('Costume deleted');
      loadCostumes();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="admin-costumes-page">
      {/* Header */}
      <div className="border-b border-stone-800 bg-stone-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Link
              to={backTo}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
              data-testid="admin-costumes-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <div className="flex items-center gap-2">
              <Shirt className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-bold">Costume Shop Editor</h1>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-stone-800/60 p-1 rounded-full">
            <button
              onClick={() => setTab('products')}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold ${
                tab === 'products' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
              data-testid="admin-costumes-tab-products"
            >
              <Package className="w-4 h-4" /> Products
            </button>
            <button
              onClick={() => setTab('orders')}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold ${
                tab === 'orders' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
              data-testid="admin-costumes-tab-orders"
            >
              <ClipboardList className="w-4 h-4" /> Orders
            </button>
          </div>

          {tab === 'products' ? (
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90"
              data-testid="admin-costumes-new"
            >
              <Plus className="w-4 h-4" /> New Costume
            </button>
          ) : (
            <button
              onClick={loadOrders}
              className="inline-flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg font-semibold text-sm"
              data-testid="admin-costumes-refresh-orders"
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {tab === 'products' && (
          loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : costumes.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-stone-700 rounded-2xl" data-testid="admin-costumes-empty">
              <Shirt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-6">No costumes yet.</p>
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-full font-semibold"
              >
                <Plus className="w-4 h-4" /> Create First Costume
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {costumes.map((c) => (
                <div
                  key={c.id}
                  className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden flex flex-col"
                  data-testid={`admin-costume-card-${c.id}`}
                >
                  <div className="relative aspect-[3/4] bg-stone-800">
                    {c.thumbnail ? (
                      <img loading="lazy" decoding="async" src={c.thumbnail} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-600">
                        <ImageIcon className="w-10 h-10" />
                      </div>
                    )}
                    {c.featured && (
                      <span className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground rounded-full text-xs font-bold">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <span className="text-xs uppercase tracking-wider text-primary">{c.category}</span>
                    <h3 className="font-semibold mt-1 line-clamp-1">{c.name}</h3>
                    <div className="text-primary font-bold mt-1">${(c.base_price ?? 0).toFixed(2)}</div>
                    {c.variants?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {c.variants.length} variant{c.variants.length === 1 ? '' : 's'}
                      </div>
                    )}
                    <div className="mt-auto pt-3 flex gap-2">
                      <button
                        onClick={() => openEdit(c)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm"
                        data-testid={`admin-costume-edit-${c.id}`}
                      >
                        <Edit2 className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => remove(c.id, c.name)}
                        className="inline-flex items-center justify-center p-2 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded-lg"
                        data-testid={`admin-costume-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === 'orders' && (
          <OrdersPanel
            orders={orders}
            loading={loadingOrders}
            onStatusChange={updateOrderStatus}
          />
        )}
      </div>

      {/* Edit / Create Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="admin-costume-form">
          <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
              <h2 className="text-lg font-bold">
                {editingId ? 'Edit Costume' : 'New Costume'}
              </h2>
              <button
                onClick={closeForm}
                className="p-2 hover:bg-stone-800 rounded-lg"
                data-testid="admin-costume-form-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm text-muted-foreground">Name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-name"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-muted-foreground">Category</span>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-category"
                  >
                    {CATEGORY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="text-sm text-muted-foreground">Description</span>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                  data-testid="admin-costume-description"
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm text-muted-foreground">Base Price (USD)</span>
                  <input
                    type="number"
                    step="0.01"
                    value={form.base_price}
                    onChange={(e) => setForm({ ...form, base_price: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-price"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-muted-foreground">Delivery (days)</span>
                  <input
                    type="number"
                    value={form.estimated_delivery_days}
                    onChange={(e) => setForm({ ...form, estimated_delivery_days: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-delivery"
                  />
                </label>

                <div className="flex items-center gap-4 pt-6">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.featured}
                      onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                      data-testid="admin-costume-featured"
                    />
                    <span className="text-sm">Featured</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.is_custom_order}
                      onChange={(e) => setForm({ ...form, is_custom_order: e.target.checked })}
                      data-testid="admin-costume-custom"
                    />
                    <span className="text-sm">Custom Order</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm text-muted-foreground">Sizes (comma-separated)</span>
                  <input
                    type="text"
                    value={sizesInput}
                    onChange={(e) => setSizesInput(e.target.value)}
                    placeholder="XS, S, M, L, XL"
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-sizes"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-muted-foreground">Colors (comma-separated)</span>
                  <input
                    type="text"
                    value={colorsInput}
                    onChange={(e) => setColorsInput(e.target.value)}
                    placeholder="Red, Gold, White"
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-colors"
                  />
                </label>

                <label className="block">
                  <span className="text-sm text-muted-foreground">Tags (comma-separated)</span>
                  <input
                    type="text"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    placeholder="carnival, traditional"
                    className="w-full mt-1 px-3 py-2 bg-stone-800 rounded-lg border border-stone-700 focus:border-primary focus:outline-none"
                    data-testid="admin-costume-tags"
                  />
                </label>
              </div>

              {/* Variants */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Variants (per size/color stock & price)</span>
                    <p className="text-xs text-stone-500">Final price = base price + variant adjustment.</p>
                  </div>
                  <button
                    onClick={addVariant}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm"
                    data-testid="admin-costume-variant-add"
                  >
                    <Plus className="w-4 h-4" /> Add Variant
                  </button>
                </div>
                {form.variants.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No variants. Base price applies.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-2 text-xs uppercase tracking-wider text-stone-500 px-2">
                      <span>Size</span>
                      <span>Color</span>
                      <span>Price Adj</span>
                      <span>Stock</span>
                      <span>SKU</span>
                      <span></span>
                    </div>
                    {form.variants.map((v, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-2 bg-stone-800/60 border border-stone-700 rounded-lg p-2"
                        data-testid={`admin-costume-variant-row-${i}`}
                      >
                        <input
                          type="text"
                          value={v.size}
                          onChange={(e) => updateVariant(i, 'size', e.target.value)}
                          placeholder="M"
                          className="px-2 py-1.5 bg-stone-900 rounded border border-stone-700 focus:border-primary focus:outline-none text-sm"
                          data-testid={`admin-costume-variant-size-${i}`}
                        />
                        <input
                          type="text"
                          value={v.color || ''}
                          onChange={(e) => updateVariant(i, 'color', e.target.value)}
                          placeholder="Red"
                          className="px-2 py-1.5 bg-stone-900 rounded border border-stone-700 focus:border-primary focus:outline-none text-sm"
                          data-testid={`admin-costume-variant-color-${i}`}
                        />
                        <input
                          type="number"
                          step="0.01"
                          value={v.price_adjustment}
                          onChange={(e) => updateVariant(i, 'price_adjustment', e.target.value)}
                          placeholder="0.00"
                          className="px-2 py-1.5 bg-stone-900 rounded border border-stone-700 focus:border-primary focus:outline-none text-sm"
                          data-testid={`admin-costume-variant-price-${i}`}
                        />
                        <input
                          type="number"
                          value={v.stock}
                          onChange={(e) => updateVariant(i, 'stock', e.target.value)}
                          placeholder="0"
                          className="px-2 py-1.5 bg-stone-900 rounded border border-stone-700 focus:border-primary focus:outline-none text-sm"
                          data-testid={`admin-costume-variant-stock-${i}`}
                        />
                        <input
                          type="text"
                          value={v.sku || ''}
                          onChange={(e) => updateVariant(i, 'sku', e.target.value)}
                          placeholder="SKU"
                          className="px-2 py-1.5 bg-stone-900 rounded border border-stone-700 focus:border-primary focus:outline-none text-sm"
                          data-testid={`admin-costume-variant-sku-${i}`}
                        />
                        <button
                          onClick={() => removeVariant(i)}
                          className="inline-flex items-center justify-center p-2 bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded"
                          data-testid={`admin-costume-variant-remove-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Images */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Images</span>
                    <p className="text-xs text-stone-500">Drag to reorder. Click to set cover.</p>
                  </div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg cursor-pointer text-sm">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading…' : 'Upload Images'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="admin-costume-upload"
                      disabled={uploading}
                    />
                  </label>
                </div>
                {form.images.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No images added.</div>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={form.images} strategy={rectSortingStrategy}>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="admin-costume-images-grid">
                        {form.images.map((url) => (
                          <SortableImage
                            key={url}
                            url={url}
                            isCover={form.thumbnail === url}
                            onSetCover={setThumbnail}
                            onRemove={removeImage}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-stone-800">
              <button
                onClick={closeForm}
                className="px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg text-sm"
                data-testid="admin-costume-cancel"
              >
                Cancel
              </button>
              <button
                onClick={save}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90"
                data-testid="admin-costume-save"
              >
                <Save className="w-4 h-4" /> {editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sortable image tile
function SortableImage({ url, isCover, onSetCover, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${isCover ? 'border-primary' : 'border-stone-700'}`}
      data-testid="admin-costume-image-tile"
    >
      <img loading="lazy" decoding="async"
        src={url}
        alt=""
        className="w-full h-full object-cover cursor-pointer"
        onClick={() => onSetCover(url)}
      />
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 p-1 bg-black/70 hover:bg-black/90 rounded cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
        data-testid="admin-costume-image-drag"
      >
        <GripVertical className="w-3 h-3 text-white" />
      </button>
      <button
        onClick={() => onRemove(url)}
        className="absolute top-1 right-1 p-1 bg-black/70 hover:bg-red-600 rounded"
        data-testid="admin-costume-image-remove"
      >
        <X className="w-3 h-3" />
      </button>
      {isCover && (
        <span className="absolute bottom-1 left-1 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] rounded-full font-bold">
          Cover
        </span>
      )}
    </div>
  );
}

// Orders Panel
function OrdersPanel({ orders, loading, onStatusChange }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-20 border border-dashed border-stone-700 rounded-2xl" data-testid="admin-costume-orders-empty">
        <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No costume orders yet.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="admin-costume-orders-list">
      {orders.map((o) => (
        <div
          key={o.id}
          className="bg-stone-900 border border-stone-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-4 items-center"
          data-testid={`admin-costume-order-${o.id}`}
        >
          <div>
            <div className="font-semibold">{o.order_number}</div>
            <div className="text-xs text-muted-foreground">
              {o.customer_name} · {o.customer_email}
            </div>
            <div className="text-xs text-stone-500 mt-1">
              {new Date(o.created_at).toLocaleString()}
            </div>
          </div>
          <div className="text-sm text-stone-300">
            {(o.items || []).map((it, i) => (
              <div key={i} className="truncate">
                {it.costume_name} — {it.size || '—'}{it.color ? ` / ${it.color}` : ''} × {it.quantity}
              </div>
            ))}
            {o.is_local_pickup && <div className="text-xs text-emerald-400 mt-1">Local pickup</div>}
          </div>
          <div className="flex flex-col items-start md:items-end gap-1">
            <span className="text-primary font-bold">${(o.total ?? 0).toFixed(2)}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${PAYMENT_BADGE[o.payment_status] || 'bg-stone-500/20 text-stone-300'}`}>
              {o.payment_status}
            </span>
          </div>
          <select
            value={o.fulfillment_status || 'pending'}
            onChange={(e) => onStatusChange(o.id, e.target.value)}
            className="px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-sm focus:border-primary focus:outline-none"
            data-testid={`admin-costume-order-status-${o.id}`}
          >
            {FULFILLMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}
